import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  DollarSign,
  Receipt,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const expenseCategories = [
  { value: "materials", label: "Materials" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "labor", label: "Labor" },
  { value: "delivery", label: "Delivery" },
  { value: "other", label: "Other" },
];

const expenseStatusOptions = [
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
];

const invoiceStatusOptions = [
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "paid", label: "Paid" },
];

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [expenseSheetOpen, setExpenseSheetOpen] = useState(false);
  const [invoiceSheetOpen, setInvoiceSheetOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState("");
  const [expenseForm, setExpenseForm] = useState({
    project_id: "",
    category: "materials",
    description: "",
    amount: 0,
    date: new Date().toISOString().split("T")[0],
    invoice_number: "",
    status: "pending",
    notes: "",
  });
  const [invoiceForm, setInvoiceForm] = useState({
    project_id: "",
    client_name: "",
    amount: 0,
    status: "draft",
    due_date: "",
    notes: "",
  });

  const fetchData = async () => {
    try {
      const [expensesRes, invoicesRes, projectsRes] = await Promise.all([
        axios.get(`${API}/expenses`, { withCredentials: true }),
        axios.get(`${API}/expenses/invoices`, { withCredentials: true }),
        axios.get(`${API}/projects`, { withCredentials: true }),
      ]);
      setExpenses(expensesRes.data);
      setInvoices(invoicesRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...expenseForm, amount: parseFloat(expenseForm.amount) || 0 };
      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.expense_id}`, payload, {
          withCredentials: true,
        });
        toast.success("Expense updated");
      } else {
        await axios.post(`${API}/expenses`, payload, {
          withCredentials: true,
        });
        toast.success("Expense created");
      }
      setExpenseSheetOpen(false);
      resetExpenseForm();
      fetchData();
    } catch (error) {
      console.error("Error saving expense:", error);
      toast.error("Failed to save expense");
    }
  };

  const handleInvoiceSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...invoiceForm, amount: parseFloat(invoiceForm.amount) || 0 };
      if (editingInvoice) {
        await axios.put(`${API}/expenses/invoices/${editingInvoice.invoice_id}`, payload, {
          withCredentials: true,
        });
        toast.success("Invoice updated");
      } else {
        await axios.post(`${API}/expenses/invoices`, payload, {
          withCredentials: true,
        });
        toast.success("Invoice created");
      }
      setInvoiceSheetOpen(false);
      resetInvoiceForm();
      fetchData();
    } catch (error) {
      console.error("Error saving invoice:", error);
      toast.error("Failed to save invoice");
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      const endpoint =
        deleteType === "expense"
          ? `${API}/expenses/${itemToDelete.expense_id}`
          : `${API}/expenses/invoices/${itemToDelete.invoice_id}`;
      await axios.delete(endpoint, { withCredentials: true });
      toast.success(`${deleteType === "expense" ? "Expense" : "Invoice"} deleted`);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Failed to delete");
    }
  };

  const resetExpenseForm = () => {
    setEditingExpense(null);
    setExpenseForm({
      project_id: "",
      category: "materials",
      description: "",
      amount: 0,
      date: new Date().toISOString().split("T")[0],
      invoice_number: "",
      status: "pending",
      notes: "",
    });
  };

  const resetInvoiceForm = () => {
    setEditingInvoice(null);
    setInvoiceForm({
      project_id: "",
      client_name: "",
      amount: 0,
      status: "draft",
      due_date: "",
      notes: "",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
      case "sent":
        return "bg-blue-50 text-blue-700 ring-blue-600/20";
      default:
        return "bg-slate-50 text-slate-700 ring-slate-600/20";
    }
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalInvoiced = invoices.reduce((sum, i) => sum + (i.amount || 0), 0);
  const paidInvoices = invoices.filter((i) => i.status === "paid").reduce((sum, i) => sum + (i.amount || 0), 0);

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="expenses-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
          Expenses & Invoicing
        </h1>
        <p className="text-slate-500 mt-1">Track project costs and client invoices</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-red-50 rounded-sm">
              <TrendingDown className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-slate-900">
                R {totalExpenses.toLocaleString()}
              </p>
              <p className="text-sm text-slate-500">Total Expenses</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-blue-50 rounded-sm">
              <Receipt className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-slate-900">
                R {totalInvoiced.toLocaleString()}
              </p>
              <p className="text-sm text-slate-500">Total Invoiced</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-emerald-50 rounded-sm">
              <TrendingUp className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-slate-900">
                R {paidInvoices.toLocaleString()}
              </p>
              <p className="text-sm text-slate-500">Received</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="expenses" className="space-y-6">
        <div className="flex items-center justify-between">
          <TabsList className="bg-slate-100 rounded-sm">
            <TabsTrigger value="expenses" className="rounded-sm" data-testid="expenses-tab">
              Expenses ({expenses.length})
            </TabsTrigger>
            <TabsTrigger value="invoices" className="rounded-sm" data-testid="invoices-tab">
              Invoices ({invoices.length})
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Expenses Tab */}
        <TabsContent value="expenses">
          <Card className="rounded-sm border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-lg">Expenses</CardTitle>
              <Button
                size="sm"
                className="rounded-sm bg-slate-900 hover:bg-slate-800"
                onClick={() => setExpenseSheetOpen(true)}
                data-testid="add-expense-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Expense
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {expenses.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenses.map((expense) => (
                      <TableRow 
                        key={expense.expense_id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => {
                          setEditingExpense(expense);
                          setExpenseForm({
                            ...expense,
                            invoice_number: expense.invoice_number || "",
                            notes: expense.notes || "",
                          });
                          setExpenseSheetOpen(true);
                        }}
                        data-testid={`expense-row-${expense.expense_id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                              <DollarSign className="h-5 w-5 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                {expense.description}
                              </p>
                              {expense.invoice_number && (
                                <p className="text-sm text-slate-500">
                                  #{expense.invoice_number}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {projects.find((p) => p.project_id === expense.project_id)?.name || "-"}
                        </TableCell>
                        <TableCell>
                          <Badge className="rounded-sm bg-slate-100 text-slate-700">
                            {expense.category}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500">{expense.date}</TableCell>
                        <TableCell>
                          <Badge className={`rounded-sm ${getStatusColor(expense.status)}`}>
                            {expense.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          R {expense.amount?.toLocaleString()}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => {
                                  setEditingExpense(expense);
                                  setExpenseForm({
                                    ...expense,
                                    invoice_number: expense.invoice_number || "",
                                    notes: expense.notes || "",
                                  });
                                  setExpenseSheetOpen(true);
                                }}
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setItemToDelete(expense);
                                  setDeleteType("expense");
                                  setDeleteDialogOpen(true);
                                }}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-16">
                  <DollarSign className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No expenses recorded yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card className="rounded-sm border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-lg">Invoices</CardTitle>
              <Button
                size="sm"
                className="rounded-sm bg-slate-900 hover:bg-slate-800"
                onClick={() => setInvoiceSheetOpen(true)}
                data-testid="add-invoice-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Create Invoice
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {invoices.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.invoice_id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                              <Receipt className="h-5 w-5 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                {invoice.client_name}
                              </p>
                              {invoice.notes && (
                                <p className="text-sm text-slate-500 truncate max-w-xs">
                                  {invoice.notes}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {projects.find((p) => p.project_id === invoice.project_id)?.name || "-"}
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {invoice.due_date || "-"}
                        </TableCell>
                        <TableCell>
                          <Badge className={`rounded-sm ${getStatusColor(invoice.status)}`}>
                            {invoice.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          R {invoice.amount?.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => {
                                  setEditingInvoice(invoice);
                                  setInvoiceForm({
                                    ...invoice,
                                    due_date: invoice.due_date || "",
                                    notes: invoice.notes || "",
                                  });
                                  setInvoiceSheetOpen(true);
                                }}
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setItemToDelete(invoice);
                                  setDeleteType("invoice");
                                  setDeleteDialogOpen(true);
                                }}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-16">
                  <Receipt className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No invoices created yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Expense Sheet */}
      <Sheet
        open={expenseSheetOpen}
        onOpenChange={(open) => {
          setExpenseSheetOpen(open);
          if (!open) resetExpenseForm();
        }}
      >
        <SheetContent className="sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingExpense ? "Edit Expense" : "Add Expense"}</SheetTitle>
            <SheetDescription>
              {editingExpense ? "Update expense details" : "Record a new expense"}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleExpenseSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>Project *</Label>
              <Select
                value={expenseForm.project_id}
                onValueChange={(value) =>
                  setExpenseForm({ ...expenseForm, project_id: value })
                }
                required
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.project_id} value={project.project_id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                value={expenseForm.description}
                onChange={(e) =>
                  setExpenseForm({ ...expenseForm, description: e.target.value })
                }
                placeholder="Enter description"
                required
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={expenseForm.category}
                  onValueChange={(value) =>
                    setExpenseForm({ ...expenseForm, category: value })
                  }
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseCategories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={expenseForm.status}
                  onValueChange={(value) =>
                    setExpenseForm({ ...expenseForm, status: value })
                  }
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseStatusOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount (R) *</Label>
                <Input
                  id="amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={expenseForm.amount}
                  onChange={(e) =>
                    setExpenseForm({ ...expenseForm, amount: e.target.value })
                  }
                  required
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={expenseForm.date}
                  onChange={(e) =>
                    setExpenseForm({ ...expenseForm, date: e.target.value })
                  }
                  required
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="invoice_number">Invoice Number</Label>
              <Input
                id="invoice_number"
                value={expenseForm.invoice_number}
                onChange={(e) =>
                  setExpenseForm({ ...expenseForm, invoice_number: e.target.value })
                }
                placeholder="Enter invoice number"
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={expenseForm.notes}
                onChange={(e) =>
                  setExpenseForm({ ...expenseForm, notes: e.target.value })
                }
                placeholder="Additional notes"
                rows={2}
                className="rounded-sm"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setExpenseSheetOpen(false);
                  resetExpenseForm();
                }}
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800"
              >
                {editingExpense ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Invoice Sheet */}
      <Sheet
        open={invoiceSheetOpen}
        onOpenChange={(open) => {
          setInvoiceSheetOpen(open);
          if (!open) resetInvoiceForm();
        }}
      >
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{editingInvoice ? "Edit Invoice" : "Create Invoice"}</SheetTitle>
            <SheetDescription>
              {editingInvoice ? "Update invoice details" : "Create a new client invoice"}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleInvoiceSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>Project *</Label>
              <Select
                value={invoiceForm.project_id}
                onValueChange={(value) =>
                  setInvoiceForm({ ...invoiceForm, project_id: value })
                }
                required
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.project_id} value={project.project_id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="client_name">Client Name *</Label>
              <Input
                id="client_name"
                value={invoiceForm.client_name}
                onChange={(e) =>
                  setInvoiceForm({ ...invoiceForm, client_name: e.target.value })
                }
                placeholder="Enter client name"
                required
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount (R) *</Label>
                <Input
                  id="amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={invoiceForm.amount}
                  onChange={(e) =>
                    setInvoiceForm({ ...invoiceForm, amount: e.target.value })
                  }
                  required
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={invoiceForm.status}
                  onValueChange={(value) =>
                    setInvoiceForm({ ...invoiceForm, status: value })
                  }
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {invoiceStatusOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="due_date">Due Date</Label>
              <Input
                id="due_date"
                type="date"
                value={invoiceForm.due_date}
                onChange={(e) =>
                  setInvoiceForm({ ...invoiceForm, due_date: e.target.value })
                }
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={invoiceForm.notes}
                onChange={(e) =>
                  setInvoiceForm({ ...invoiceForm, notes: e.target.value })
                }
                placeholder="Additional notes"
                rows={2}
                className="rounded-sm"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setInvoiceSheetOpen(false);
                  resetInvoiceForm();
                }}
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800"
              >
                {editingInvoice ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteType === "expense" ? "Expense" : "Invoice"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this{" "}
              {deleteType === "expense" ? "expense" : "invoice"}? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Expenses;
